// AssistantStarter — ONE recommendation the assistant offers before the first
// message, in BOTH places it offers them (Nika 2026-09-26: one centred
// component): the empty chat's stack (Chat.js Empty.Starters) and the chip
// row of the AI island above the collapsed composer (Suggestions.js Actions,
// `compact`).
//
// Row state (childrenAs:'state' — produced by the workspace package's
// `workspaceStarters(root)` / `buildSuggestions(root)`, or Chat.js's own
// generic fallback):
//   { key, text, prompt?, icon?, caption?, localAction?, generic? }
//   text        — what the capsule shows AND, when `prompt` is absent, what it
//                 sends (the island's rows name it `label`; both are read)
//   prompt      — the fuller ask that actually goes to the AI
//   icon        — sprite name of the app the prompt concerns ('magicstar' when
//                 the prompt is about the workspace as a whole)
//   caption     — the app's name (the glyph already shows which app; the
//                 capsule carries one line of words, so the name is not
//                 painted)
//   localAction — a registered function to run instead of sending (module
//                 manifests declare these for their own starters)
//   generic     — an always-offered staple: one ink step quieter than the
//                 surface's own offers (the island's hierarchy)
//
// THE KIT'S TONAL CAPSULE (Nika 2026-09-26, "make first recommendations
// buttons in the middle and make better design to it"): AppCapsuleBtnToolbar
// — the resting `layer` wash (fill contrast, no stroke), the `selected` step
// under the pointer and on press, the house press scale, a controlH tap
// target on the pill radius, the kit's `:disabled`. The app's glyph leads in
// the quiet `caption` ink (hue never marks a starter), the prompt follows,
// the pair centred. Chat.js centres the capsules and gives them ONE width (a
// shared grid track); a long prompt wraps inside its capsule, centred, rather
// than widening it. It was a launcher row: a glyph tile, a caption line and a
// trailing arrow on a full-width card. Focus ring: the brand reset's global
// :focus-visible — never re-implemented here.
export const AssistantStarter = {
  extends: 'AppCapsuleBtnToolbar',
  gap: 'Y',
  padding: 'Y A',
  whiteSpace: 'normal',
  textAlign: 'center',
  lineHeight: '1.35',
  minWidth: '0',
  color: (el, s) => (s && s.generic ? 'paragraph' : 'title'),

  // THE GLYPH PLATE (dropdowns spec §4, §7.2 A5): the app's glyph on a borderless round plate in the quiet
  // ink; the capsule's hover and keyboard focus INVERT it (the one state logic, §5). The generic spark is the
  // line assistant glyph (`magicstar` rows keep working — they map to it).
  Plate: {
    dataGlyphPlate: true,
    ariaHidden: 'true',
    display: 'flex',
    align: 'center center',
    flexShrink: '0',
    boxSize: 'glyphPlateXs',
    round: 'radiusPill',
    color: 'caption',
    transition: 'background-color A defaultBezier, color A defaultBezier',
    Icon: {
      name: (el, s) => (s.icon && s.icon !== 'magicstar' ? s.icon : 'lnAssistant'),
      boxSize: 'iconSm'
    }
  },
  '&:hover [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },
  '&:focus-visible [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },

  StarterLabel: {
    tag: 'span',
    minWidth: '0',
    text: (el, s) => s.text || s.label
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    // Resolve this Chat instance's scope by walking up for `chatScope` —
    // inlined (no helper import) so the walk survives frank serialization,
    // the same idiom as Chat.js `ck` and ChatMessage's Suggestions row.
    let scope = 'general'
    let n = el
    while (n) {
      if (n.chatScope) {
        scope = n.chatScope
        break
      }
      n = n.parent
    }
    // THE ASK ROUTER (workspace functions/assistantAskRoute.js
    // assistantAskHandoff): a starter that clearly asks for another surface —
    // a page, an app, slides, a website, the tour — goes there, before the
    // assistant opens. Every other starter is this chat's. Only the general
    // chat routes; a starter with its own action runs that.
    const ask = s.prompt || s.text || s.label
    const fns = el.context && el.context.functions
    if (
      !s.localAction &&
      scope === 'general' &&
      ask &&
      fns &&
      typeof fns.assistantAskHandoff === 'function' &&
      el.call('assistantAskHandoff', ask, { from: 'starter' })
    ) {
      return
    }
    // From the island above the collapsed composer the answer lands in the
    // assistant, so it opens first (a no-op inside the open drawer).
    if (s.root && typeof s.root.update === 'function' && !s.root.assistantOpen) {
      s.root.update({ assistantOpen: true })
    }
    try {
      if (s.localAction) {
        el.call(s.localAction)
      } else {
        el.call('sendPrompt', s.prompt || s.text || s.label, scope)
      }
    } catch (_) {}
  }
}

// AssistantStarterList — THE centred list of starters: ONE grid track as wide
// as the longest starter in the active language (never wider than the host),
// each row the kit capsule above in its compact size — the size the AI
// island's row uses, so the one component reads the same everywhere. Rows
// arrive through `children`: { key, text, prompt?, icon?, caption?,
// generic?, localAction? }.
//
// Hosts: the empty assistant chat (Chat.Empty.Starters) and the Page view's
// empty workspace — "the composer first + starter chips from the installed
// apps" (ASSISTANT_BOARD_MODE_SPEC §4; current-23's view feeds it rows built
// from the ai.boards vocabulary). One list, so the two can never drift.
export const AssistantStarterList = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, max-content)',
  justifyContent: 'center',
  rowGap: 'Y',
  width: '100%',
  maxWidth: 'H',
  childProps: { compact: true },
  childrenAs: 'state',
  childExtends: 'AssistantStarter'
}
